/**
 * Canonical user-type handling.
 *
 * The AUTHORITATIVE list of user types is the hosted Console asset
 * `/users/user-types.json`. For this marketplace it contains exactly two:
 *
 *   yritys-tai-organisaatio  "Yritys tai organisaatio"  (company / organisation)
 *   yksityishenkilo          "Yksityishenkilö"          (private person)
 *
 * `mergeUserConfig` (util/configHelpers.js) does NOT merge local defaults in
 * production, so those two ids are the only values a signup form can ever
 * produce.
 *
 * The values below marked LEGACY were written into `publicData.userType` by our
 * own server code (server/api/telia.js used to stamp 'broker') or by earlier
 * iterations of the template. They are never selectable at signup, but existing
 * accounts still carry them, so every check has to accept them too.
 *
 * Historical note: gates used to compare against 'broker' directly. Since no
 * signup can produce 'broker', those gates silently failed open/closed for real
 * users. Always go through the helpers in this file.
 */

// Console-configured ids (authoritative).
export const USER_TYPE_COMPANY = 'yritys-tai-organisaatio';
export const USER_TYPE_PRIVATE = 'yksityishenkilo';

// Written by our own code / older template versions. Not selectable at signup.
export const LEGACY_COMPANY_USER_TYPES = ['broker', 'company'];
export const LEGACY_PRIVATE_USER_TYPES = ['buyer', 'privateSeller'];

const COMPANY_USER_TYPES = [USER_TYPE_COMPANY, ...LEGACY_COMPANY_USER_TYPES];
const PRIVATE_USER_TYPES = [USER_TYPE_PRIVATE, ...LEGACY_PRIVATE_USER_TYPES];

/**
 * Back-compat map. Prefer the predicates below: comparing a raw userType
 * against a single constant is exactly the bug this module exists to prevent.
 */
export const userTypes = {
  company: USER_TYPE_COMPANY,
  private: USER_TYPE_PRIVATE,
  // Legacy aliases, kept so existing imports keep resolving.
  buyer: 'buyer',
  broker: 'broker',
  privateSeller: 'privateSeller',
};

export const getUserType = currentUser =>
  currentUser?.attributes?.profile?.publicData?.userType ?? null;

/** True for any user type that represents a company / organisation. */
export const isCompanyUserType = userType => COMPANY_USER_TYPES.includes(userType);

/** True for any user type that represents a private person. */
export const isPrivateUserType = userType => PRIVATE_USER_TYPES.includes(userType);

/**
 * Has this user completed Telia strong identification?
 *
 * `protectedData.teliaVerification` is written server-side by the Telia callback
 * (server/api/telia.js) and is the only evidence of a verified identity that the
 * frontend can see. It is NOT a substitute for the server-side signing-rights
 * check — see server/api/create-company-profile.js.
 */
export const isTeliaVerified = currentUser => {
  const teliaVerification = currentUser?.attributes?.profile?.protectedData?.teliaVerification;
  return !!(teliaVerification && (teliaVerification.hetu || teliaVerification.personalId));
};

/**
 * Treat a user as a company when their user type says so, OR when they arrived
 * through Telia. Telia identification is only offered on the business path, and
 * some early Telia accounts were created without any userType at all.
 */
export const getIsCompany = currentUser =>
  isCompanyUserType(getUserType(currentUser)) || isTeliaVerified(currentUser);

export const getIsPrivate = currentUser => isPrivateUserType(getUserType(currentUser));

/** @deprecated use getIsCompany */
export const getIsBroker = currentUser => getIsCompany(currentUser);

/** @deprecated use getIsPrivate */
export const getIsBuyer = currentUser => {
  const userType = getUserType(currentUser);
  return userType === USER_TYPE_PRIVATE || userType === 'buyer';
};

export const getIsPrivateSeller = currentUser => getUserType(currentUser) === 'privateSeller';

export const getIsStripeVerified = currentUser =>
  currentUser?.attributes?.profile?.publicData?.isVerified ?? null;
